/**
 * src/pages/NotFound.tsx — the `*` catch-all route.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/pages/NotFound.tsx` (whole file, 25 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §1.3.
 *
 * ═══ THREE SOURCE BEHAVIOURS THAT LOOK LIKE MISTAKES AND ARE REPRODUCED ══════════════════════
 *
 *  1. **This route is not auth-gated** (`App.tsx:24`). An unauthenticated visitor to `/anything`
 *     gets this page, not the login form. Spec §1.3 records it explicitly and asks that it be
 *     preserved or changed as a stated decision — preserved.
 *
 *  2. **`console.error` on every mount** (`NotFound.tsx:7-9`), with the pathname. Not a toast, not
 *     telemetry — a raw console error in production. Message and argument order verbatim.
 *
 *  3. **"Return to Home" is a plain `<a href="/">`, not a router `<Link>`** (`NotFound.tsx:16`), so
 *     it triggers a **full page reload**: the React tree is torn down, `HexclaveAuthProvider` re-runs its
 *     `GET /api/auth/me` round trip, and the whole React Query cache is discarded. Do not convert it
 *     to `<Link>` — the reload is observable behaviour, and on this page it is also the only thing
 *     that resets a session whose token went stale (DF-22 rank 5: nothing else handles 401/403).
 *
 * The effect's dependency array is `[location.pathname]`, so navigating between two unknown paths
 * logs twice. Unchanged.
 */
import { useLocation } from 'react-router-dom'
import { useEffect } from 'react'

const NotFound = () => {
  const location = useLocation()

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname)
  }, [location.pathname])

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold">404</h1>
        <p className="mb-4 text-xl text-muted-foreground">Oops! Page not found</p>
        {/* Deliberately an <a>, not a <Link> — full reload. See the header. */}
        <a href="/" className="text-primary underline hover:text-primary/90">
          Return to Home
        </a>
      </div>
    </div>
  )
}

export default NotFound
